import { UiSurface, uiPaper, uiSurface, UiSurfaceId } from "shared/ui/uiSurface"
import { ArrowBack, Print, Undo } from "@mui/icons-material"
import {
  CalendarToday,
  CameraAlt,
  Close,
  Draw,
  EditOutlined,
  History,
  Inventory,
  LocalPhone,
  LocalShipping,
  LocationOn,
  Notes,
  Payments,
  Person,
  PersonOutline,
  Tag,
  ZoomIn,
} from "@mui/icons-material"
import {
  Alert,
  alpha,
  Box,
  Button,
  Card as MuiCard,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import dayjs from "dayjs"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router"
import {
  OrderDetail,
  OrderTraceability,
  TraceabilityProofFile,
  useGetOrderByIdQuery,
  useGetOrderManifestItemsQuery,
  useGetOrderTraceabilitiesQuery,
  useTransitionOrderMutation,
  useUndoTransitionOrderMutation,
} from "../apis/appApi"
import { MainLayout } from "./MainLayout"
import {
  orderDetailToLabel,
  PrintLabelView,
  triggerPrint,
} from "./orders/PrintLabelView"
import { TagChip } from "./TagChip"

// ---------------------------------------------------------------------------
// State -> color mapping
// ---------------------------------------------------------------------------

const stateColors: Record<string, { bg: string; fg: string }> = {
  CREATED: { bg: "#E3F2FD", fg: "#1565C0" },
  ORDER_CREATED: { bg: "#E3F2FD", fg: "#1565C0" },
  IN_PREPARATION: { bg: "#E3F2FD", fg: "#1565C0" },
  READY_FOR_HAULAGE: { bg: "#E8F5E9", fg: "#2E7D32" },
  ON_THE_WAY_TO_CUSTOMER: { bg: "#FFF3E0", fg: "#E65100" },
  ON_THE_WAY_TO_PROVIDER: { bg: "#FFF3E0", fg: "#E65100" },
  DELIVERED: { bg: "#E8F5E9", fg: "#2E7D32" },
  COMPLETED: { bg: "#E8F5E9", fg: "#2E7D32" },
  CANCELLED: { bg: "#FFEBEE", fg: "#C62828" },
  TO_PROCESS_PROVIDER: { bg: "#FFF8E1", fg: "#F57F17" },
  TO_PROCESS_PROVIDER_CLIENT: { bg: "#FFF8E1", fg: "#F57F17" },
  TO_PROCESS_PROVIDER_RECURRING: { bg: "#FFF8E1", fg: "#F57F17" },
  TO_PROCESS_PROVIDER_ERROR: { bg: "#FFEBEE", fg: "#C62828" },
}

const formatState = (state: string) =>
  state
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase())

const formatEvent = (event: string) =>
  event
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase())

const formatDate = (d: string | null | undefined) => {
  if (!d) return null
  const parsed = dayjs(d)
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : d
}

const formatDateTime = (d: string | null | undefined) => {
  if (!d) return null
  const parsed = dayjs(d)
  return parsed.isValid() ? parsed.format("MMM D, YYYY \u00b7 h:mm A") : d
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const Section: FC<{
  icon: React.ReactNode
  title: string
  children: React.ReactNode
  ui?: UiSurfaceId
}> = ({ icon, title, children, ui }) => (
  <Box {...(ui && uiSurface(ui))} sx={{ mb: 2.5 }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
      <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
      <Typography variant="subtitle2" fontWeight={600} color="text.primary">
        {title}
      </Typography>
    </Stack>
    {children}
  </Box>
)

const Field: FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="baseline"
    sx={{ py: 0.5 }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={500}
      color="text.primary"
      sx={{ textAlign: "right", ml: 2 }}
    >
      {value ?? "\u2014"}
    </Typography>
  </Stack>
)

const InfoCard: FC<{ children: React.ReactNode; sx?: object; "data-testid"?: string }> = ({
  children,
  sx,
  "data-testid": testId,
}) => (
  <Box
    data-testid={testId}
    sx={{
      bgcolor: "grey.50",
      borderRadius: 2,
      px: 2,
      py: 1.5,
      ...sx,
    }}
  >
    {children}
  </Box>
)

// ---------------------------------------------------------------------------
// Transition button color
// ---------------------------------------------------------------------------

const eventButtonColor = (
  event: string,
): "primary" | "success" | "error" | "warning" => {
  const e = event.toUpperCase()
  if (e.includes("CANCEL")) return "error"
  if (e.includes("DELIVER") || e.includes("COMPLETED")) return "success"
  if (e.includes("UNABLE") || e.includes("BACK")) return "warning"
  return "primary"
}

// ---------------------------------------------------------------------------
// Actor name helper
// ---------------------------------------------------------------------------

const getActorName = (tr: OrderTraceability): string | null => {
  if (tr.actor) {
    const parts = [tr.actor.firstName, tr.actor.lastName].filter(Boolean)
    if (parts.length > 0) return parts.join(" ")
    if (tr.actor.username) return tr.actor.username
  }
  if (tr.operatorCode) return tr.operatorCode
  return null
}

// ---------------------------------------------------------------------------
// Details Tab
// ---------------------------------------------------------------------------

const DetailsTab: FC<{
  order: OrderDetail
  t: (key: string, opts?: Record<string, unknown>) => string
}> = ({ order, t }) => {
  const sc = stateColors[order.state] ?? { bg: "#F5F5F5", fg: "#616161" }

  return (
    <UiSurface id="UI-DLV2-053">
      {/* Status & Courier card */}
      <Section
        ui="UI-DLV2-054"
        icon={<LocalShipping fontSize="small" />}
        title={t("orders.detail.statusAndCourier")}
      >
        <InfoCard data-testid="order-detail-courier-section">
          <Field
            label={t("orders.state")}
            value={
              <Chip
                label={formatState(order.state)}
                size="small"
                sx={{
                  bgcolor: sc.bg,
                  color: sc.fg,
                  fontWeight: 600,
                  fontSize: 11,
                  height: 22,
                }}
              />
            }
          />
          {order.calculatedArrivalTime && (
            <Field
              label={t("orders.detail.calculatedArrival")}
              value={formatDateTime(order.calculatedArrivalTime)}
            />
          )}
          <Field
            label={t("orders.detail.courier")}
            value={
              order.haulerEmployee ? (
                <Stack alignItems="flex-end" spacing={0.25}>
                  <Typography variant="body2" fontWeight={500}>
                    {order.haulerEmployee.firstName} {order.haulerEmployee.lastName}
                  </Typography>
                  {order.haulerEmployee.phoneNumber && (
                    <Typography variant="caption" color="text.secondary">
                      {order.haulerEmployee.phoneNumber}
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.disabled" fontStyle="italic">
                  {t("orders.detail.courierUnassigned")}
                </Typography>
              )
            }
          />
          {order.recipientIdentity && (
            <Field
              label={t("orders.detail.recipient")}
              value={
                <Stack alignItems="flex-end" spacing={0}>
                  <Typography variant="body2" fontWeight={500}>
                    {order.recipientIdentity}
                  </Typography>
                  {order.recipientRelation && (
                    <Typography variant="caption" color="text.secondary">
                      {order.recipientRelation}
                      {order.recipientRelationMore ? ` - ${order.recipientRelationMore}` : ""}
                    </Typography>
                  )}
                </Stack>
              }
            />
          )}
        </InfoCard>
      </Section>

      {/* Client */}
      {order.client && (
        <Section
          ui="UI-DLV2-055"
          icon={<Person fontSize="small" />}
          title={t("orders.detail.client")}
        >
          <InfoCard>
            <Field
              label={t("employees.firstName")}
              value={order.client.firstName}
            />
            <Field
              label={t("employees.lastName")}
              value={order.client.lastName}
            />
            {order.client.organisation && (
              <Field
                label={t("orders.detail.organisation")}
                value={order.client.organisation}
              />
            )}
            {order.client.email && (
              <Field
                label={t("orders.detail.email")}
                value={order.client.email}
              />
            )}
            {order.client.externalReference && (
              <Field
                label={t("orders.detail.externalRef")}
                value={order.client.externalReference}
              />
            )}
          </InfoCard>

          {/* Phone numbers */}
          {order.client.phoneNumbers && order.client.phoneNumbers.length > 0 && (
            <Box data-testid="order-detail-phone-numbers" sx={{ mt: 1.5 }}>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
                <LocalPhone sx={{ fontSize: 14, color: "text.secondary" }} />
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {t("orders.detail.phoneNumbers")}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                {order.client.phoneNumbers.map((phone) => (
                  <Typography key={phone.id} variant="body2" color="text.primary">
                    {phone.label ? `${phone.label}: ` : ""}
                    {phone.number}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {/* Client notes */}
          {order.client.notes && (
            <InfoCard sx={{ mt: 1.5, bgcolor: "info.50", border: 1, borderColor: "info.200" }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                {t("orders.detail.clientNotes")}
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ whiteSpace: "pre-wrap" }}>
                {order.client.notes}
              </Typography>
            </InfoCard>
          )}

          {/* Client tags */}
          {order.client.tags && order.client.tags.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                {t("orders.detail.clientTags")}
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {order.client.tags.map((ct, i) => (
                  <TagChip
                    key={ct.tagId ?? i}
                    tag={ct.tag || { label: `Tag #${ct.tagId}` }}
                    size="small"
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Section>
      )}

      {/* Address */}
      {order.address && (
        <Section
          ui="UI-DLV2-056"
          icon={<LocationOn fontSize="small" />}
          title={t("orders.detail.address")}
        >
          <InfoCard>
            <Typography variant="body2" fontWeight={500} color="text.primary">
              {[order.address.streetNumber, order.address.streetName]
                .filter(Boolean)
                .join(" ")}
              {order.address.appartNumber &&
                `, apt. ${order.address.appartNumber}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[order.address.city, order.address.state, order.address.postcode]
                .filter(Boolean)
                .join(", ")}
            </Typography>
            {order.address.label && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                {order.address.label}
              </Typography>
            )}
          </InfoCard>

          {/* Address notes (delivery instructions) */}
          {order.address.notes && (
            <InfoCard sx={{ mt: 1, bgcolor: "warning.50", border: 1, borderColor: "warning.200" }}>
              <Typography variant="caption" fontWeight={600} color="warning.dark" sx={{ display: "block", mb: 0.5 }}>
                {t("orders.detail.addressNotes")}
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ whiteSpace: "pre-wrap" }}>
                {order.address.notes}
              </Typography>
            </InfoCard>
          )}
        </Section>
      )}

      {/* Order info */}
      <Section
        ui="UI-DLV2-057"
        icon={<CalendarToday fontSize="small" />}
        title={t("orders.detail.info")}
      >
        <InfoCard>
          <Field
            label={t("orders.date")}
            value={formatDate(order.deliveryDate)}
          />
          <Field
            label={t("orders.desiredArrivalTime")}
            value={
              order.desiredArrivalTimeHour && order.desiredArrivalTimeHourEnd
                ? t("orders.detail.desiredTimeRange", {
                    time: order.desiredArrivalTime?.replace(/_/g, " "),
                    start: order.desiredArrivalTimeHour,
                    end: order.desiredArrivalTimeHourEnd,
                  })
                : order.desiredArrivalTime?.replace(/_/g, " ")
            }
          />
          {order.externalReference && (
            <Field
              label={t("orders.detail.externalRef")}
              value={order.externalReference}
            />
          )}
          <Field
            label={t("orders.amount")}
            value={
              order.amount != null ? (
                <Typography
                  component="span"
                  variant="body2"
                  fontWeight={700}
                  color="text.primary"
                >
                  ${order.amount.toFixed(2)}
                </Typography>
              ) : null
            }
          />
          {order.paymentType && (
            <Field
              label={t("orders.detail.paymentType")}
              value={order.paymentType.replace(/_/g, " ")}
            />
          )}
          {order.collectedAmount != null && (
            <Field
              label={t("orders.detail.collectedAmount")}
              value={`$${order.collectedAmount.toFixed(2)}`}
            />
          )}
          {order.totalAmount != null && (
            <Field
              label={t("orders.detail.totalAmount")}
              value={`$${order.totalAmount.toFixed(2)}`}
            />
          )}
          {order.callTime && (
            <Field
              label={t("orders.detail.callTime")}
              value={formatDateTime(order.callTime)}
            />
          )}
          <Field
            label={t("orders.dateCreated")}
            value={formatDateTime(order.dateCreated)}
          />
        </InfoCard>
      </Section>

      {/* Order Tags */}
      {order.tags && order.tags.length > 0 && (
        <Section
          ui="UI-DLV2-058"
          icon={<Tag fontSize="small" />}
          title={t("orders.detail.tags")}
        >
          <Stack
            direction="row"
            spacing={0.5}
            flexWrap="wrap"
            useFlexGap
          >
            {order.tags.map((jt, i) => (
              <TagChip
                key={jt.tagId ?? i}
                tag={jt.tag || { label: `Tag #${jt.tagId}` }}
                size="small"
              />
            ))}
          </Stack>
        </Section>
      )}

      {/* Bags */}
      {order.bags && order.bags.length > 0 && (
        <Section
          ui="UI-DLV2-059"
          icon={<Payments fontSize="small" />}
          title={t("orders.detail.bags")}
        >
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {order.bags.map((bag, i) => (
              <Chip
                key={bag.id ?? i}
                label={`${bag.bagType?.shortName || bag.bagType?.name || "Bag"} \u00d7 ${bag.number ?? 1}`}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        </Section>
      )}

      {/* Manifest Items (F060) */}
      {order.manifestId != null && (
        <ManifestSection orderId={order.id} t={t} />
      )}

      {/* Order Notes */}
      {order.notes && (
        <Section
          ui="UI-DLV2-061"
          icon={<Notes fontSize="small" />}
          title={t("orders.detail.notes")}
        >
          <InfoCard sx={{ bgcolor: "warning.50" }}>
            <Typography
              data-testid="order-detail-notes"
              variant="body2"
              color="text.primary"
              sx={{ whiteSpace: "pre-wrap" }}
            >
              {order.notes}
            </Typography>
          </InfoCard>
        </Section>
      )}

      {/* Timestamps */}
      <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
        <Stack direction="row" spacing={3}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CalendarToday sx={{ fontSize: 14, color: "text.disabled" }} />
            <Typography variant="caption" color="text.disabled">
              Created {formatDateTime(order.dateCreated)}
            </Typography>
          </Stack>
          {order.lastUpdated && (
            <Typography variant="caption" color="text.disabled">
              Updated {formatDateTime(order.lastUpdated)}
            </Typography>
          )}
        </Stack>
      </Box>
    </UiSurface>
  )
}

// ---------------------------------------------------------------------------
// Manifest Section (F060) — fetches manifest items for an order
// ---------------------------------------------------------------------------

const ManifestSection: FC<{
  orderId: number
  t: (key: string, opts?: Record<string, unknown>) => string
}> = ({ orderId, t }) => {
  const { data, isLoading } = useGetOrderManifestItemsQuery(orderId)
  const manifest = data?.data

  if (isLoading) {
    return (
      <Section
        icon={<Inventory fontSize="small" />}
        title={t("orders.detail.manifest")}
      >
        <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
      </Section>
    )
  }

  if (!manifest || manifest.items.length === 0) {
    return null
  }

  const getStatusChip = (accepted: boolean | null | undefined) => {
    if (accepted === true) {
      return (
        <Chip
          label={t("orders.detail.manifest.accepted")}
          size="small"
          color="success"
          variant="outlined"
          sx={{ height: 20, fontSize: 11 }}
        />
      )
    }
    if (accepted === false) {
      return (
        <Chip
          label={t("orders.detail.manifest.rejected")}
          size="small"
          color="error"
          variant="outlined"
          sx={{ height: 20, fontSize: 11 }}
        />
      )
    }
    return (
      <Chip
        label={t("orders.detail.manifest.pending")}
        size="small"
        variant="outlined"
        sx={{ height: 20, fontSize: 11 }}
      />
    )
  }

  return (
    <Section
      ui="UI-DLV2-060"
      icon={<Inventory fontSize="small" />}
      title={t("orders.detail.manifest")}
    >
      {manifest.confirmationText?.text && (
        <InfoCard sx={{ mb: 1.5, bgcolor: "info.50", border: 1, borderColor: "info.200" }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            {t("orders.detail.manifest.confirmationText")}
          </Typography>
          <Typography variant="body2" color="text.primary">
            {manifest.confirmationText.text}
          </Typography>
        </InfoCard>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75 }}>
                {t("orders.detail.manifest.itemLabel")}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75 }}>
                {t("orders.detail.manifest.itemOrigin")}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12, py: 0.75 }}>
                {t("orders.detail.manifest.itemStatus")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {manifest.items.map((mi) => (
              <TableRow key={mi.id}>
                <TableCell sx={{ py: 0.75 }}>
                  <Typography variant="body2">{mi.item.label}</Typography>
                </TableCell>
                <TableCell sx={{ py: 0.75 }}>
                  <Typography variant="body2" color="text.secondary">
                    {mi.item.origin}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ py: 0.75 }}>
                  {getStatusChip(mi.acceptedOnDelivery)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Section>
  )
}

// ---------------------------------------------------------------------------
// Proof helpers: build image src from file data
// ---------------------------------------------------------------------------

/**
 * Converts a file data field to a displayable image src.
 * The data may be:
 * - A full data URI (data:image/png;base64,...)
 * - Raw base64 (no prefix)
 * - An HTTP URL
 */
const toImageSrc = (data: string, mimeType?: string | null): string => {
  if (data.startsWith("data:")) return data
  if (data.startsWith("http")) return data
  // Assume raw base64
  const mime = mimeType || "image/png"
  return `data:${mime};base64,${data}`
}

// ---------------------------------------------------------------------------
// Photo Lightbox Dialog
// ---------------------------------------------------------------------------

const PhotoLightbox: FC<{
  open: boolean
  src: string | null
  onClose: () => void
}> = ({ open, src, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="md"
    fullWidth
    PaperProps={{ ...uiPaper("UI-DLV2-063"), sx: { bgcolor: "grey.900", overflow: "hidden" } }}
  >
    <Box sx={{ position: "relative" }}>
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          color: "white",
          bgcolor: "rgba(0,0,0,0.5)",
          "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          zIndex: 1,
        }}
      >
        <Close />
      </IconButton>
      {src && (
        <Box
          component="img"
          src={src}
          alt="Delivery proof"
          sx={{
            width: "100%",
            maxHeight: "80vh",
            objectFit: "contain",
            display: "block",
          }}
        />
      )}
    </Box>
  </Dialog>
)

// ---------------------------------------------------------------------------
// Delivery Proof Display (signature + photos for one traceability entry)
// ---------------------------------------------------------------------------

const ProofDisplay: FC<{
  tr: OrderTraceability
  t: (key: string, opts?: Record<string, unknown>) => string
  onOpenPhoto: (src: string) => void
}> = ({ tr, t, onOpenPhoto }) => {
  const hasSignature = !!tr.signature?.data
  const hasPhotos = !!tr.photos && tr.photos.length > 0

  if (!hasSignature && !hasPhotos) return null

  return (
    <Box sx={{ mt: 1 }}>
      {/* Signature */}
      {hasSignature && tr.signature && (
        <Box sx={{ mb: hasPhotos ? 1 : 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
            <Draw sx={{ fontSize: 13, color: "text.secondary" }} />
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {t("orders.detail.proof.signature")}
            </Typography>
          </Stack>
          <Box
            sx={{
              display: "inline-block",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 0.5,
              bgcolor: "white",
              cursor: "pointer",
              "&:hover": { borderColor: "primary.main" },
            }}
            onClick={() =>
              onOpenPhoto(toImageSrc(tr.signature!.data, tr.signature!.mimeType))
            }
          >
            <Box
              component="img"
              src={toImageSrc(tr.signature!.data, tr.signature!.mimeType)}
              alt={t("orders.detail.proof.signature")}
              sx={{
                maxWidth: 200,
                maxHeight: 80,
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        </Box>
      )}

      {/* Photos */}
      {hasPhotos && tr.photos && (
        <Box>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
            <CameraAlt sx={{ fontSize: 13, color: "text.secondary" }} />
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {t("orders.detail.proof.photos", { count: tr.photos.length })}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {tr.photos.map((photo: TraceabilityProofFile) => (
              <Box
                key={photo.id}
                sx={{
                  position: "relative",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                  cursor: "pointer",
                  width: 64,
                  height: 64,
                  "&:hover": {
                    borderColor: "primary.main",
                    "& .zoom-icon": { opacity: 1 },
                  },
                }}
                onClick={() =>
                  onOpenPhoto(toImageSrc(photo.data, photo.mimeType))
                }
              >
                <Box
                  component="img"
                  src={toImageSrc(photo.data, photo.mimeType)}
                  alt={photo.filename || t("orders.detail.proof.photo")}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
                <Box
                  className="zoom-icon"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(0,0,0,0.3)",
                    opacity: 0,
                    transition: "opacity 0.15s",
                  }}
                >
                  <ZoomIn sx={{ color: "white", fontSize: 20 }} />
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  )
}

// ---------------------------------------------------------------------------
// History Tab
// ---------------------------------------------------------------------------

const HistoryTab: FC<{
  traceabilities: OrderTraceability[]
  t: (key: string, opts?: Record<string, unknown>) => string
}> = ({ traceabilities, t }) => {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null)

  const handleOpenPhoto = useCallback((src: string) => {
    setLightboxSrc(src)
  }, [])

  if (traceabilities.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <History sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {t("orders.detail.noEvents")}
        </Typography>
      </Box>
    )
  }

  // Parse ORDER_EDITION message into structured changes
  const parseEditionChanges = (
    message: string | null,
  ): Array<{ field: string; old: string | null; new: string | null }> | null => {
    if (!message || !message.startsWith("{")) return null
    try {
      const parsed = JSON.parse(message)
      if (parsed?.changes && Array.isArray(parsed.changes)) return parsed.changes
    } catch {
      // fall through
    }
    return null
  }

  const getFieldLabel = (field: string): string => {
    const key = `orders.detail.editionFields.${field}`
    const label = t(key)
    // If i18n returns the key itself, fall back to the raw field name
    return label === key ? field : label
  }

  return (
    <>
      <Stack {...uiSurface("UI-DLV2-062")} spacing={0}>
        {traceabilities.map((tr, i) => {
          const isEdition = tr.event === "ORDER_EDITION"
          const editionChanges = isEdition ? parseEditionChanges(tr.message) : null
          const srcColor =
            stateColors[tr.sourceState] ?? { bg: "#F5F5F5", fg: "#616161" }
          const tgtColor =
            stateColors[tr.targetState] ?? { bg: "#F5F5F5", fg: "#616161" }
          const actorName = getActorName(tr)

          return (
            <Box
              key={tr.id}
              sx={{
                display: "flex",
                gap: 1.5,
                py: 1,
                borderBottom: i < traceabilities.length - 1 ? 1 : 0,
                borderColor: "divider",
              }}
            >
              {/* Timeline dot/icon + line */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  pt: 0.5,
                }}
              >
                {isEdition ? (
                  <EditOutlined
                    sx={{ fontSize: 16, color: "info.main", flexShrink: 0 }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: tgtColor.fg,
                      flexShrink: 0,
                    }}
                  />
                )}
                {i < traceabilities.length - 1 && (
                  <Box
                    sx={{
                      width: 1,
                      flex: 1,
                      bgcolor: "divider",
                      mt: 0.5,
                    }}
                  />
                )}
              </Box>
              {/* Content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {isEdition ? (
                  // --- Edition entry ---
                  <>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="info.main"
                      sx={{ display: "block" }}
                    >
                      {t("orders.detail.orderEdited")}
                    </Typography>
                    {editionChanges ? (
                      <Box
                        sx={{
                          mt: 0.5,
                          pl: 1,
                          borderLeft: 2,
                          borderColor: "info.light",
                        }}
                      >
                        {editionChanges.map((ch, ci) => (
                          <Typography
                            key={ci}
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", lineHeight: 1.6 }}
                          >
                            <strong>{getFieldLabel(ch.field)}:</strong>{" "}
                            <span style={{ textDecoration: "line-through", opacity: 0.6 }}>
                              {ch.old ?? t("orders.detail.editionValueNone")}
                            </span>
                            {" \u2192 "}
                            <span style={{ fontWeight: 600 }}>
                              {ch.new ?? t("orders.detail.editionValueNone")}
                            </span>
                          </Typography>
                        ))}
                      </Box>
                    ) : tr.message ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", fontStyle: "italic", mt: 0.25 }}
                      >
                        {tr.message}
                      </Typography>
                    ) : null}
                  </>
                ) : (
                  // --- Transition entry (existing behavior) ---
                  <>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Chip
                        label={formatState(tr.sourceState)}
                        size="small"
                        sx={{
                          bgcolor: srcColor.bg,
                          color: srcColor.fg,
                          fontWeight: 600,
                          fontSize: 10,
                          height: 20,
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ mx: 0.25 }}
                      >
                        {"\u2192"}
                      </Typography>
                      <Chip
                        label={formatState(tr.targetState)}
                        size="small"
                        sx={{
                          bgcolor: tgtColor.bg,
                          color: tgtColor.fg,
                          fontWeight: 600,
                          fontSize: 10,
                          height: 20,
                        }}
                      />
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.25 }}
                    >
                      {formatEvent(tr.event)}
                    </Typography>
                    {tr.message && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          fontStyle: "italic",
                          mt: 0.25,
                        }}
                      >
                        {tr.message}
                      </Typography>
                    )}
                  </>
                )}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                  <Typography variant="caption" color="text.disabled">
                    {formatDateTime(tr.dateTime)}
                  </Typography>
                  {actorName && (
                    <>
                      <Typography variant="caption" color="text.disabled">
                        {"\u00b7"}
                      </Typography>
                      <Stack direction="row" spacing={0.25} alignItems="center">
                        <PersonOutline sx={{ fontSize: 12, color: "text.disabled" }} />
                        <Typography variant="caption" color="text.disabled">
                          {t("orders.detail.traceabilityBy", { name: actorName })}
                        </Typography>
                      </Stack>
                    </>
                  )}
                </Stack>
                {tr.interlocutorIdentity && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                    {t("orders.detail.recipient")}: {tr.interlocutorIdentity}
                    {tr.interlocutorRelation ? ` (${tr.interlocutorRelation})` : ""}
                  </Typography>
                )}

                {/* Delivery proof: signature + photos */}
                <ProofDisplay tr={tr} t={t} onOpenPhoto={handleOpenPhoto} />
              </Box>
            </Box>
          )
        })}
      </Stack>

      {/* Photo lightbox dialog */}
      <PhotoLightbox
        open={!!lightboxSrc}
        src={lightboxSrc}
        onClose={() => setLightboxSrc(null)}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const OrderDetailPage: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const orderId = Number(id)

  const {
    data: response,
    isLoading,
    error,
  } = useGetOrderByIdQuery(orderId, { skip: !orderId })
  const { data: traceResponse } = useGetOrderTraceabilitiesQuery(orderId, {
    skip: !orderId,
  })

  const [activeTab, setActiveTab] = useState(0)

  const [transitionOrder, { isLoading: isTransitioning }] =
    useTransitionOrderMutation()
  const [undoTransitionOrder, { isLoading: isUndoing }] =
    useUndoTransitionOrderMutation()

  const [confirmEvent, setConfirmEvent] = useState<string | null>(null)
  const [operatorCode, setOperatorCode] = useState("")
  const [transitionMessage, setTransitionMessage] = useState("")
  const [transitionResult, setTransitionResult] = useState<{
    previousState: string
    newState: string
  } | null>(null)
  const [transitionError, setTransitionError] = useState<string | null>(null)

  // Undo state (F056)
  const [showUndoConfirm, setShowUndoConfirm] = useState(false)
  const [undoOperatorCode, setUndoOperatorCode] = useState("")
  const [undoMessage, setUndoMessage] = useState("")

  const order: OrderDetail | undefined = response?.data
  const possibleEvents: string[] = response?.possibleEvents ?? []
  const canUndo: boolean = response?.canUndo ?? false
  const undoTargetState: string | null = response?.undoTargetState ?? null
  const traceabilities: OrderTraceability[] = traceResponse?.data ?? []

  const handleTransition = async () => {
    if (!confirmEvent || !orderId || !operatorCode.trim()) return
    setTransitionError(null)
    try {
      const result = await transitionOrder({
        id: orderId,
        event: confirmEvent,
        operatorCode: operatorCode.trim(),
        ...(transitionMessage.trim() ? { message: transitionMessage.trim() } : {}),
      }).unwrap()
      setTransitionResult({
        previousState: result.previousState,
        newState: result.newState,
      })
      setConfirmEvent(null)
      setOperatorCode("")
      setTransitionMessage("")
    } catch (err: any) {
      setTransitionError(
        err?.data?.message || err?.message || "Transition failed",
      )
      setConfirmEvent(null)
    }
  }

  const handleUndo = async () => {
    if (!orderId || !undoOperatorCode.trim()) return
    setTransitionError(null)
    try {
      const result = await undoTransitionOrder({
        id: orderId,
        operatorCode: undoOperatorCode.trim(),
        ...(undoMessage.trim() ? { message: undoMessage.trim() } : {}),
      }).unwrap()
      setTransitionResult({
        previousState: result.previousState,
        newState: result.newState,
      })
      setShowUndoConfirm(false)
      setUndoOperatorCode("")
      setUndoMessage("")
    } catch (err: any) {
      setTransitionError(
        err?.data?.message || err?.message || t("orders.detail.undoError"),
      )
      setShowUndoConfirm(false)
    }
  }

  const sc = stateColors[order?.state ?? ""] ?? { bg: "#F5F5F5", fg: "#616161" }

  const breadcrumbs = [
    { label: t("orders.title"), path: "/orders" },
    { label: order?.reference ?? `#${id}` },
  ]

  if (isLoading) {
    return (
      <MainLayout breadcrumbs={breadcrumbs}>
        <Box sx={{ p: 3 }}>
          {/* Header skeleton */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="text" width={200} height={32} />
            <Skeleton variant="rounded" width={100} height={24} />
          </Stack>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
              <Skeleton variant="rounded" height={90} sx={{ mb: 2 }} />
              <Skeleton variant="rounded" height={90} sx={{ mb: 2 }} />
              <Skeleton variant="rounded" height={60} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Skeleton variant="rounded" height={200} />
            </Grid>
          </Grid>
        </Box>
      </MainLayout>
    )
  }

  if (error || !order) {
    return (
      <MainLayout breadcrumbs={breadcrumbs}>
        <Box sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <IconButton onClick={() => navigate("/orders")} size="small">
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" fontWeight={600}>
              {t("orders.detail.title", { id })}
            </Typography>
          </Stack>
          <Alert severity="error">Order not found</Alert>
        </Box>
      </MainLayout>
    )
  }

  return (
    <MainLayout breadcrumbs={breadcrumbs}>
      {/* Print label (hidden on screen, shown on print) */}
      <PrintLabelView labels={[orderDetailToLabel(order)]} />

      <Box className="no-print" sx={{ p: 3 }}>
        {/* Page header */}
        <Stack {...uiSurface("UI-DLV2-051")} direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate("/orders")} size="small">
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 1, lineHeight: 1.2 }}
            >
              {order.reference}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: -0.25 }}>
              {order.client
                ? `${order.client.firstName} ${order.client.lastName}`
                : t("orders.detail.title", { id: order.id })}
            </Typography>
          </Box>
          <Chip
            data-testid="order-detail-state"
            label={formatState(order.state)}
            size="small"
            sx={{
              bgcolor: sc.bg,
              color: sc.fg,
              fontWeight: 600,
              fontSize: 12,
            }}
          />
          <Chip
            data-testid="order-detail-workflow"
            label={order.stateMachineWorkflow}
            size="small"
            variant="outlined"
            sx={{ fontSize: 11 }}
          />
          {order.source && (
            <Chip
              label={order.source.replace(/_/g, " ").toLowerCase()}
              size="small"
              variant="outlined"
              sx={{ fontSize: 11 }}
            />
          )}
          <Tooltip title={t("print.printLabel")}>
            <IconButton
              data-testid="order-detail-print-btn"
              size="small"
              onClick={() => triggerPrint()}
              className="no-print"
            >
              <Print fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Alerts */}
        {transitionResult && (
          <Alert
            data-testid="order-detail-transition-success"
            severity="success"
            onClose={() => setTransitionResult(null)}
            sx={{ mb: 2 }}
          >
            {formatState(transitionResult.previousState)} {"\u2192"}{" "}
            {formatState(transitionResult.newState)}
          </Alert>
        )}
        {transitionError && (
          <Alert
            data-testid="order-detail-transition-error"
            severity="error"
            onClose={() => setTransitionError(null)}
            sx={{ mb: 2 }}
          >
            {transitionError}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Main content with tabs */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Box
              {...uiSurface("UI-DLV2-052")}
              sx={{
                bgcolor: alpha(sc.bg, 0.3),
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
              }}
            >
              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={activeTab}
                  onChange={(_e, v) => setActiveTab(v)}
                  sx={{ px: 2.5, minHeight: 44 }}
                >
                  <Tab
                    data-testid="order-detail-tab-details"
                    label={t("orders.detail.tabs.details")}
                    sx={{ minHeight: 44, textTransform: "none", fontWeight: 600 }}
                  />
                  <Tab
                    data-testid="order-detail-tab-history"
                    label={`${t("orders.detail.tabs.history")} (${traceabilities.length})`}
                    sx={{ minHeight: 44, textTransform: "none", fontWeight: 600 }}
                  />
                </Tabs>
              </Box>

              {/* Tab content */}
              <Box sx={{ px: 2.5, py: 2.5 }}>
                {activeTab === 0 && <DetailsTab order={order} t={t} />}
                {activeTab === 1 && (
                  <HistoryTab traceabilities={traceabilities} t={t} />
                )}
              </Box>
            </Box>
          </Grid>

          {/* Sidebar: Transition actions */}
          <Grid size={{ xs: 12, md: 4 }}>
            <MuiCard {...uiSurface("UI-DLV2-064")}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("orders.detail.possibleEvents")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t("orders.state")}: {formatState(order.state)}
                </Typography>
                <Stack spacing={1}>
                  {possibleEvents.length > 0 ? (
                    possibleEvents.map((evt: string) => (
                      <Button
                        data-testid={`order-transition-${evt}-btn`}
                        key={evt}
                        variant="outlined"
                        size="small"
                        fullWidth
                        color={eventButtonColor(evt)}
                        disabled={isTransitioning}
                        onClick={() => setConfirmEvent(evt)}
                      >
                        {formatEvent(evt)}
                      </Button>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("orders.detail.noEvents")}
                    </Typography>
                  )}
                </Stack>
                {/* Undo button (F056) */}
                {canUndo && undoTargetState && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
                    <Button
                      data-testid="order-undo-transition-btn"
                      variant="outlined"
                      color="warning"
                      size="small"
                      fullWidth
                      startIcon={<Undo />}
                      disabled={isTransitioning || isUndoing}
                      onClick={() => setShowUndoConfirm(true)}
                    >
                      {t("orders.detail.undoTransition")}
                    </Button>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: "block", textAlign: "center" }}
                    >
                      {t("orders.detail.undoTargetState", {
                        state: formatState(undoTargetState),
                      })}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </MuiCard>
          </Grid>
        </Grid>

        {/* Confirm dialog */}
        <Dialog
          slotProps={{ paper: uiPaper("UI-DLV2-065") }}
          data-testid="order-transition-dialog"
          open={!!confirmEvent}
          onClose={() => setConfirmEvent(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>{t("orders.detail.transition")}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2.5 }}>
              {t("orders.detail.confirmTransition", {
                event: confirmEvent ? formatEvent(confirmEvent) : "",
              })}
            </DialogContentText>
            <Stack spacing={2}>
              <TextField
                label={t("ordersPage.employeeCode")}
                required
                fullWidth
                value={operatorCode}
                onChange={(e) => setOperatorCode(e.target.value)}
                placeholder={t("ordersPage.employeeCodePlaceholder")}
                error={!operatorCode.trim()}
                helperText={!operatorCode.trim() ? t("ordersPage.employeeCodeRequired") : ""}
                autoFocus
              />
              <TextField
                label={t("ordersPage.transitionMessage")}
                multiline
                minRows={3}
                maxRows={6}
                fullWidth
                value={transitionMessage}
                onChange={(e) => setTransitionMessage(e.target.value)}
                placeholder={t("ordersPage.transitionMessagePlaceholder")}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              data-testid="order-transition-cancel-btn"
              onClick={() => { setConfirmEvent(null); setOperatorCode(""); setTransitionMessage("") }}
            >
              {t("cancel")}
            </Button>
            <Button
              data-testid="order-transition-confirm-btn"
              onClick={handleTransition}
              variant="contained"
              color={confirmEvent ? eventButtonColor(confirmEvent) : "primary"}
              disabled={isTransitioning || !operatorCode.trim()}
            >
              {t("confirm")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Undo confirm dialog (F056) */}
        <Dialog
          slotProps={{ paper: uiPaper("UI-DLV2-066") }}
          data-testid="order-undo-dialog"
          open={showUndoConfirm}
          onClose={() => setShowUndoConfirm(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>{t("orders.detail.undoTransition")}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2.5 }}>
              {t("orders.detail.undoConfirmMessage", {
                currentState: formatState(order.state),
                previousState: undoTargetState ? formatState(undoTargetState) : "",
              })}
            </DialogContentText>
            <Stack spacing={2}>
              <TextField
                label={t("ordersPage.employeeCode")}
                required
                fullWidth
                value={undoOperatorCode}
                onChange={(e) => setUndoOperatorCode(e.target.value)}
                placeholder={t("ordersPage.employeeCodePlaceholder")}
                error={!undoOperatorCode.trim()}
                helperText={!undoOperatorCode.trim() ? t("ordersPage.employeeCodeRequired") : ""}
                autoFocus
              />
              <TextField
                label={t("ordersPage.transitionMessage")}
                multiline
                minRows={2}
                maxRows={4}
                fullWidth
                value={undoMessage}
                onChange={(e) => setUndoMessage(e.target.value)}
                placeholder={t("ordersPage.transitionMessagePlaceholder")}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              data-testid="order-undo-cancel-btn"
              onClick={() => {
                setShowUndoConfirm(false)
                setUndoOperatorCode("")
                setUndoMessage("")
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              data-testid="order-undo-confirm-btn"
              onClick={handleUndo}
              variant="contained"
              color="warning"
              disabled={isUndoing || !undoOperatorCode.trim()}
            >
              {t("confirm")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  )
}
